"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import SmallCta from "./SmallCta";

const DEFAULT_FEATURES = [
	{
		number: "/01",
		titleKey: "feature1Title",
		descKey: "feature1Desc",
		image: "/image/new-version/machining/five-axis/why/high-quality-parts.jpg",
		bgColor: "bg-[#000]",
		textColor: "text-white",
	},
	{
		number: "/02",
		titleKey: "feature2Title",
		descKey: "feature2Desc",
		image: "/image/new-version/machining/five-axis/why/fast-lead-time.jpg",
		bgColor: "bg-[#107e03]",
		textColor: "text-white",
	},
	{
		number: "/03",
		titleKey: "feature3Title",
		descKey: "feature3Desc",
		image: "/image/new-version/machining/five-axis/why/manufacturing-expertise​.jpg",
		bgColor: "bg-[#9ec687]",
		textColor: "text-white",
	},
] as const;

export interface FeatureCardConfig {
	number: string;
	titleKey: string;
	descKey: string;
	image: string;
	bgColor?: string;
	textColor?: string;
}

export interface WhyChooseUsProps {
	translationNamespace?: string;
	customFeatures?: FeatureCardConfig[];
	showFooter?: boolean;
	showDescription?: boolean;
	variant?: "default" | "simple";
	className?: string;
}

export default function WhyChooseUs({
	translationNamespace = "fiveAxisWhyChoose",
	customFeatures,
	showFooter = false,
	showDescription = true,
	variant = "default",
	className = "",
}: WhyChooseUsProps) {
	const t = useTranslations(translationNamespace);

	const features = customFeatures || DEFAULT_FEATURES;
	const isSimple = variant === "simple";

	return (
		<section
			className={`w-full ${className || "py-8 sm:py-10 md:py-12 lg:py-16"} ${
				isSimple ? "bg-white" : "bg-[#ededed]"
			}`}
		>
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					{showDescription && (
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					)}
				</div>

				<div className="grid gap-6 md:grid-cols-3">
					{features.map((feature, index) => (
						<div key={index} className={`flex flex-col overflow-hidden ${feature.bgColor || "bg-white"}`}>
							<div className="relative aspect-[4/2] w-full overflow-hidden">
								<Image
									src={feature.image}
									alt={t(feature.titleKey)}
									fill
									className="object-fill"
									sizes="(max-width: 768px) 100vw, 33vw"
								/>
							</div>

							<div className="flex flex-1 flex-col p-6 md:p-8">
								<span className="mb-3 text-sm font-medium text-[#5a9f22]">{feature.number}</span>
								<h3
									className={`mb-3 text-lg font-bold sm:text-xl md:text-2xl ${
										feature.textColor || "text-black"
									}`}
								>
									{t(feature.titleKey)}
								</h3>
								<p className={`text-sm leading-relaxed md:text-base ${feature.textColor || "text-black"}`}>
									{t(feature.descKey)}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
			<div className="container">
				<SmallCta translationNamespace="cncMillingCta" />
			</div>
		</section>
	);
}
